import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth';
import { NavBar } from '../../components/NavBar';
import { SharedFooter } from '../../components/SharedFooter';
import { authHeaders, jsonAuthHeaders, signInMessageForAuthStatus } from '../../shared/authHeaders';
import { formatMoneyCompact } from '../../shared/money';
import type { FinancialTransaction, FinancialsOverview, FinancialTransactionType } from '@modelcast/types';

type TransactionFilter = 'all' | 'incoming' | 'outgoing';

interface StripeConnectStatus {
  hasAccount: boolean;
  onboardingComplete: boolean;
  detailsSubmitted?: boolean;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
}

function formatDate(dateString: string | null) {
  if (!dateString) return 'Unknown';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function typeLabel(type: FinancialTransactionType) {
  switch (type) {
    case 'ticket_purchase':
      return 'Ticket';
    case 'event_payout':
      return 'Event payout';
    case 'reference_pack_purchase':
      return 'Reference pack';
    case 'reference_pack_sale':
      return 'Pack sale';
    case 'tip_sent':
      return 'Tip sent';
    case 'tip_received':
      return 'Tip received';
  }
}

function statusLabel(transaction: FinancialTransaction) {
  if (transaction.status === 'refunded') return 'Refunded';
  if (transaction.status === 'failed') return 'Failed';
  if (transaction.payoutStatus === 'pending') return 'Payout pending';
  if (transaction.payoutStatus === 'failed') return 'Payout retrying';
  if (transaction.status === 'pending') return 'Pending';
  return 'Completed';
}

function statusClass(transaction: FinancialTransaction) {
  if (transaction.status === 'refunded' || transaction.status === 'failed' || transaction.payoutStatus === 'failed') {
    return 'bg-warn/10 text-warn';
  }
  if (transaction.status === 'pending' || transaction.payoutStatus === 'pending') {
    return 'bg-tag-soft text-tag';
  }
  return 'pill-accent';
}

function amountPrefix(transaction: FinancialTransaction) {
  if (transaction.status === 'refunded') return '';
  return transaction.direction === 'incoming' ? '+' : '-';
}

function StripePayoutSetupCard({
  getToken,
}: {
  getToken: () => Promise<string | null>;
}) {
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = async () => {
    setLoadingStatus(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch('/api/stripe/connect/status', {
        headers: authHeaders(token),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load payout setup status');
      }
      setStatus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payout setup status');
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    void loadStatus();
  }, []);

  const handleConnect = async () => {
    setWorking(true);
    setError(null);
    try {
      const token = await getToken();

      if (!status?.hasAccount) {
        const createResponse = await fetch('/api/stripe/connect/create-account', {
          method: 'POST',
          headers: jsonAuthHeaders(token),
        });
        const createData = await createResponse.json().catch(() => ({}));
        if (!createResponse.ok) {
          const authMessage = signInMessageForAuthStatus(createResponse, 'Sign in to set up payouts');
          if (authMessage) throw new Error(authMessage);
          throw new Error(createData.error || 'Failed to create Stripe account');
        }
      }

      const linkResponse = await fetch('/api/stripe/connect/create-account-link', {
        method: 'POST',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({
          returnUrl: `${window.location.origin}/financials?stripe=success`,
          refreshUrl: `${window.location.origin}/financials?stripe=refresh`,
        }),
      });
      const linkData = await linkResponse.json().catch(() => ({}));
      if (!linkResponse.ok) {
        const authMessage = signInMessageForAuthStatus(linkResponse, 'Sign in to set up payouts');
        if (authMessage) throw new Error(authMessage);
        throw new Error(linkData.error || 'Failed to create Stripe onboarding link');
      }

      window.location.href = linkData.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start Stripe setup');
      setWorking(false);
    }
  };

  const handleOpenDashboard = async () => {
    setWorking(true);
    setError(null);
    try {
      const token = await getToken();

      const response = await fetch('/api/stripe/connect/create-login-link', {
        method: 'POST',
        headers: authHeaders(token),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const authMessage = signInMessageForAuthStatus(response, 'Sign in to manage payouts');
        if (authMessage) throw new Error(authMessage);
        throw new Error(data.error || 'Failed to open Stripe dashboard');
      }

      window.open(data.url, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open Stripe dashboard');
    } finally {
      setWorking(false);
    }
  };

  const isComplete = Boolean(status?.onboardingComplete);
  const hasStarted = Boolean(status?.hasAccount);

  return (
    <section className="bg-panel border border-line rounded-xl p-5 mb-8">
      <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-display text-xl font-normal">Payout information</h2>
            {isComplete && (
              <span className="pill pill-accent text-[10px]">Connected</span>
            )}
          </div>
          <p className="text-sm text-text-dim mt-1">
            {loadingStatus
              ? 'Checking your Stripe payout setup...'
              : isComplete
                ? 'Stripe is ready to send payouts to your bank account.'
                : hasStarted
                  ? 'Finish your Stripe setup so pending and future payouts can be sent.'
                  : 'Set up Stripe to receive event, reference pack, and tip payouts.'}
          </p>
          {!loadingStatus && status?.hasAccount && !isComplete && (
            <div className="flex flex-wrap gap-2 mt-3 text-xs text-text-dim">
              <span className={`pill text-[10px] ${status.detailsSubmitted ? 'pill-accent' : 'bg-bg-secondary text-text-dim'}`}>
                Details {status.detailsSubmitted ? 'submitted' : 'needed'}
              </span>
              <span className={`pill text-[10px] ${status.chargesEnabled ? 'pill-accent' : 'bg-bg-secondary text-text-dim'}`}>
                Charges {status.chargesEnabled ? 'enabled' : 'pending'}
              </span>
              <span className={`pill text-[10px] ${status.payoutsEnabled ? 'pill-accent' : 'bg-bg-secondary text-text-dim'}`}>
                Payouts {status.payoutsEnabled ? 'enabled' : 'pending'}
              </span>
            </div>
          )}
          {error && (
            <p className="text-sm text-warn mt-3">{error}</p>
          )}
        </div>

        <div className="flex flex-wrap gap-2 shrink-0">
          <button
            type="button"
            onClick={loadStatus}
            disabled={loadingStatus || working}
            className="btn btn-sm disabled:opacity-50"
          >
            Refresh
          </button>
          {isComplete ? (
            <button
              type="button"
              onClick={handleOpenDashboard}
              disabled={working}
              className="btn btn-sm btn-accent disabled:opacity-50"
            >
              {working ? 'Opening...' : 'Open Stripe'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleConnect}
              disabled={loadingStatus || working}
              className="btn btn-sm btn-accent disabled:opacity-50"
            >
              {working
                ? 'Opening Stripe...'
                : hasStarted
                  ? 'Continue setup'
                  : 'Set up payouts'}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

export function FinancialsPage() {
  const { getToken } = useAuth();
  const [overview, setOverview] = useState<FinancialsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<TransactionFilter>('all');

  useEffect(() => {
    let cancelled = false;

    async function loadFinancials() {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const response = await fetch('/api/financials/overview', {
          headers: authHeaders(token),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'Failed to load financials');
        }
        if (!cancelled) {
          setOverview(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load financials');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadFinancials();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const transactions = useMemo(() => {
    if (!overview) return [];
    if (filter === 'all') return overview.transactions;
    return overview.transactions.filter((transaction) => transaction.direction === filter);
  }, [overview, filter]);

  return (
    <div className="min-h-screen flex flex-col bg-bg pt-[72px]">
      <NavBar variant="light" />

      <main className="page-main">
        <div className="page-container">
          <div className="page-header">
            <h1 className="page-heading">
              Financials
            </h1>
            <p className="text-sm text-text-dim mt-2">
              Tickets, payouts, reference packs, and tips tied to your account.
            </p>
          </div>

          {loading && (
            <div className="bg-panel border border-line rounded-xl p-6 text-sm text-text-dim">
              Loading financials...
            </div>
          )}

          {error && !loading && (
            <div className="bg-panel border border-line rounded-xl p-6">
              <h2 className="font-display text-xl font-normal">Unable to load financials</h2>
              <p className="text-sm text-warn mt-2">{error}</p>
            </div>
          )}

          {overview && !loading && !error && (
            <>
              <StripePayoutSetupCard getToken={getToken} />

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
                <div className="rounded-lg bg-panel border border-line p-4">
                  <div className="text-[11px] text-text-faint">Received</div>
                  <div className="font-display text-[28px] text-text">
                    {formatMoneyCompact(overview.summary.receivedCents, 'USD')}
                  </div>
                </div>
                <div className="rounded-lg bg-panel border border-line p-4">
                  <div className="text-[11px] text-text-faint">Spent</div>
                  <div className="font-display text-[28px] text-text">
                    {formatMoneyCompact(overview.summary.spentCents, 'USD')}
                  </div>
                </div>
                <div className="rounded-lg bg-panel border border-line p-4">
                  <div className="text-[11px] text-text-faint">Pending</div>
                  <div className="font-display text-[28px] text-text">
                    {formatMoneyCompact(overview.summary.pendingCents, 'USD')}
                  </div>
                </div>
                <div className="rounded-lg bg-panel border border-line p-4">
                  <div className="text-[11px] text-text-faint">Refunded</div>
                  <div className="font-display text-[28px] text-text">
                    {formatMoneyCompact(overview.summary.refundedCents, 'USD')}
                  </div>
                </div>
              </div>

              <section className="bg-panel border border-line rounded-xl p-5">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between mb-5">
                  <div>
                    <h2 className="font-display text-xl font-normal">Transactions</h2>
                    <p className="text-sm text-text-dim mt-1">
                      {overview.summary.transactionCount} historical record{overview.summary.transactionCount === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="inline-flex rounded-lg border border-line bg-bg-secondary p-1">
                    {(['all', 'incoming', 'outgoing'] as const).map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setFilter(option)}
                        className={`px-3 py-1.5 rounded-md text-sm capitalize transition-colors ${
                          filter === option
                            ? 'bg-accent text-white'
                            : 'text-text-dim hover:text-text'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>

                {transactions.length === 0 ? (
                  <div className="rounded-lg bg-bg-secondary border border-line-soft p-6 text-sm text-text-dim">
                    No transactions found.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-line-soft text-left text-[11px] uppercase tracking-[0.12em] text-text-faint">
                          <th className="py-2 pr-4 font-medium">Date</th>
                          <th className="py-2 pr-4 font-medium">Transaction</th>
                          <th className="py-2 pr-4 font-medium">Status</th>
                          <th className="py-2 font-medium text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((transaction) => (
                          <tr key={transaction.id} className="border-b border-line-soft last:border-0">
                            <td className="py-4 pr-4 text-text-dim whitespace-nowrap">
                              {formatDate(transaction.occurredAt)}
                            </td>
                            <td className="py-4 pr-4">
                              <div className="flex items-start gap-3">
                                <span className="pill bg-bg-secondary text-text-dim text-[10px] mt-0.5">
                                  {typeLabel(transaction.type)}
                                </span>
                                <div>
                                  {transaction.href ? (
                                    <Link to={transaction.href} className="font-medium text-text hover:text-accent">
                                      {transaction.title}
                                    </Link>
                                  ) : (
                                    <div className="font-medium text-text">{transaction.title}</div>
                                  )}
                                  {transaction.counterpartyName && (
                                    <div className="text-xs text-text-dim mt-1">
                                      {transaction.direction === 'incoming' ? 'From' : 'To'} {transaction.counterpartyName}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="py-4 pr-4">
                              <span className={`pill text-[10px] ${statusClass(transaction)}`}>
                                {statusLabel(transaction)}
                              </span>
                            </td>
                            <td className={`py-4 text-right font-medium whitespace-nowrap ${
                              transaction.direction === 'incoming' && transaction.status !== 'refunded'
                                ? 'text-accent'
                                : 'text-text'
                            }`}>
                              {amountPrefix(transaction)}
                              {formatMoneyCompact(transaction.amountCents, transaction.currency)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </main>

      <SharedFooter />
    </div>
  );
}
